import { Select, Table, TableColumnsType } from "antd";
import moment from "moment";
import QueryString from "qs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from "react-router-dom";
import { getSurveyQuestions } from "../../../api/surveyQuestion.api";
import Button from "../../../components/Button";
import ErrorResponse from "../../../components/ErrorResponse";
import SimpleHeader from "../../../components/layout/SimpleHeader";
import Loader from "../../../components/Loader";
import Pagination from "../../../components/Pagination";
import SelectCampus from "../../../components/SelectCampus";
import useAuth from "../../../hooks/useAuth";
import useAxios from "../../../hooks/useAxios";
import config from "../../../lib/config";
import { getCommonCodeLabel } from "../../../lib/store";
import { IGetSurveyQuestions, ISurveyQuestionItem } from "../../../models/surveyQuestion.model";
import { setCurrentCampus } from "../../../store/auth.slice";
import { RootState } from "../../../store/rootReducer";
import useUtil from "../../../hooks/useUtil";

const SurveyQuestion = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { isCampusDeactived } = useUtil();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });

  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // HP0007 = 설문문항 카테고리
  const HP0007 = commonCodes.find(item => item.commonCode === 'HP0007')?.detailCode;  
  const { data: res, isLoading, error, execute } = useAxios<IGetSurveyQuestions>();

  const [surveyCategory, setSurveyCategory] = useState<string>();
  const [title, setTitle] = useState('');
  const [page, setPage] = useState(1);
  const [params, setParams] = useState({});
  const [campusDeactived, setCampusDeactived] = useState(false);

  useEffect(() => {
    setCampusDeactived(isCampusDeactived());
  }, [isCampusDeactived]);

  useEffect(() => {
    if (currentCampus) {
      const _params: any = {
        pageNo: page,
        perPage: config.pageSize,
        campusCode: currentCampus,
      };

      if (query.page) {
        _params.pageNo = Number(query.page);
        setPage(_params.pageNo);
      }
      if (query.campusCode) {
        _params.campusCode = String(query.campusCode);
        dispatch(setCurrentCampus(_params.campusCode));
      }
      if (query.surveyCategory) {
        _params.surveyCategory = String(query.surveyCategory);
        setSurveyCategory(_params.surveyCategory);
      }
      if (query.title) {
        _params.title = String(query.title);
        setTitle(_params.title);
      }

      execute(getSurveyQuestions(_params));
      _params.page = _params.pageNo;
      delete _params.pageNo;
      delete _params.perPage;
      setParams(_params);
    }
  }, [currentCampus, location]);

  const onSearch = () => {
    const params: any = {
      page: 1,
      campusCode: currentCampus,
    };

    if (surveyCategory) params.surveyCategory = surveyCategory;
    if (title) params.title = title;

    history.push(`/homepage/survey-question?${QueryString.stringify(params)}`);
  };

  const columns: TableColumnsType<ISurveyQuestionItem> = [
    {
      title: t('homepage.archive.column1'),
      dataIndex: 'questionCode',
      key: 'questionCode',
      align: 'center',
      width: '6%',
    },
    {
      title: t('common.column.category'),
      dataIndex: 'surveyCategory',
      key: 'surveyCategory',
      align: 'center',
      width: '10%',
      render: value => getCommonCodeLabel('HP0007', value, 'ko'),
    },
    {
      title: t('homepage.survey.column12'),
      dataIndex: 'title',
      key: 'title',
      align: 'center',
      ellipsis: true,
      render: value => {
        return <div className="text-left ant-table-cell-ellipsis">{value}</div>
      }
    },
    {
      title: t('common.menu.campus'),
      dataIndex: 'campusKorName',
      key: 'campusKorName',
      align: 'center',
      width: '10%',
    },
    {
      title: t('homepage.archive.column5'),
      dataIndex: 'regDate',
      key: 'regDate',
      align: 'center',
      width: '10%',
      render: value => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('common.column.writer'),
      dataIndex: 'regMemKorName',
      key: 'regMemKorName',
      align: 'center',
      width: '10%',
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-homepage-big.svg"
      parentMenu={t('common.menu.homepage')}
      currentMenu={t('homepage.surveyQuestion.title1')}
      onlyTitle={true}
    />	  
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label>
            <div>
              <SelectCampus />
            </div>
          </li>
          <li>
            <label>{t('common.column.category')}</label>
            <div>
              <Select
                placeholder={t('common.column.category')}
                value={surveyCategory}
                onChange={value => setSurveyCategory(value)}
              >
                <Select.Option value="">{t('common.text.all')}</Select.Option>
                {HP0007?.map((item, index) => (
                  <Select.Option
                    key={index}
                    value={item.detailCode}
                  >
                    {item.korName}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </li>
          <li>
            <label>{t('homepage.survey.column12')}</label>
            <input
              type="text"
              className="form-control w-240p"
              placeholder={t('homepage.survey.placeholder1')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
              />
              <div className="comm-btn__area">
                <Button
                  className="btn-basic small black"
                  onClick={onSearch}
                  text={t('common.button.search')}
                />
              </div>
          </li>
        </ul>
      </div>
    </div>

    <div className="content scrollbar">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />

      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">{t('common.text.count')}: <b>{res?.total}</b>{t('common.text.unit')}</div>
          <div className="comm-btn__area">
            <Button
              className="btn-basic pink"
              onClick={() => history.push('/homepage/survey-question/create')}
              text={t('event.education.button1')}
              disabled={campusDeactived}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={res?.data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={record => {
              return {
                onClick: () => {
                  history.push(
                    `/homepage/survey-question/${record.questionCode}${location.search}`,
                  );
                },
              };
            }}
            rowClassName="pointer"
            rowKey="noticeCode"
          />
        </div>
        <div className="comm-bx__between">
          <div />
          <div className="common-paging center">
            <Pagination
              count={res?.total || 0}
              page={page}
              param={params}
              path="/homepage/survey-question"
            />
          </div>
          <div className="comm-btn__area right">
            <Button
              className="btn-basic pink"
              onClick={() => history.push('/homepage/survey-question/create')}
              text={t('event.education.button1')}
              disabled={campusDeactived}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default SurveyQuestion;
